import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BookOpenCheck, Pencil, Plus, Trash2 } from 'lucide-react';
import type { StyleExample } from '@mailpilot/shared';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { relativeTime, fullDate } from '../../lib/format';
import { Badge, Button, Card, CardDivider, CardHeader, EmptyState, Field, InlineError, Input, Modal, Textarea, useConfirm, useToast } from '../ui';
import { TipIconButton } from './SettingsPrimitives';

function ExampleModal({
  open,
  onClose,
  title,
  initial,
  onSubmit,
  pending,
  error,
  submitLabel,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  initial: { context: string; text: string };
  onSubmit: (v: { context: string; text: string }) => void;
  pending: boolean;
  error: unknown;
  submitLabel: string;
}) {
  const [context, setContext] = useState(initial.context);
  const [text, setText] = useState(initial.text);
  const valid = text.trim().length > 0;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description="Examples are retrieved as references when the assistant drafts similar replies."
      size="lg"
      footer={
        <>
          <Button onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="example-form" loading={pending} disabled={!valid}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <form
        id="example-form"
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) onSubmit({ context: context.trim(), text: text.trim() });
        }}
      >
        <Field label="Context" hint="What this reply responds to, in a few words. e.g. “Customer asks about delivery times”.">
          {(id) => <Input id={id} value={context} onChange={(e) => setContext(e.target.value)} data-autofocus />}
        </Field>
        <Field label="Approved reply text" required>
          {(id) => <Textarea id={id} rows={8} value={text} onChange={(e) => setText(e.target.value)} />}
        </Field>
        <InlineError error={error} />
      </form>
    </Modal>
  );
}

function ExampleRow({ ex, onEdit, onDelete, deleting }: { ex: StyleExample; onEdit: () => void; onDelete: () => void; deleting: boolean }) {
  const isCorrection = ex.kind === 'correction';
  return (
    <li className="group px-6 py-5 transition-colors hover:bg-raised/40">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={isCorrection ? 'warn' : 'primary'}>{isCorrection ? 'Correction' : 'Reply example'}</Badge>
            <span className="text-xs text-muted" title={fullDate(ex.createdAt)}>
              Added {relativeTime(ex.createdAt)}
            </span>
          </div>
          {ex.context && <p className="text-[0.8125rem] font-medium text-ink">{ex.context}</p>}
        </div>
        <div className="flex shrink-0 items-center transition-opacity [@media(hover:hover)]:opacity-50 group-hover:opacity-100 group-focus-within:opacity-100">
          <TipIconButton size="sm" label="Edit example" icon={<Pencil className="h-4 w-4" aria-hidden />} onClick={onEdit} />
          <TipIconButton size="sm" label="Delete example" tipPlacement="bottom-end" icon={<Trash2 className="h-4 w-4" aria-hidden />} onClick={onDelete} disabled={deleting} className="hover:!bg-danger-soft hover:!text-danger-ink" />
        </div>
      </div>
      {isCorrection && ex.original ? (
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <div>
            <p className="mb-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-danger-ink">AI draft</p>
            <p className="max-h-56 overflow-y-auto whitespace-pre-wrap rounded-xl bg-danger-soft/60 p-3 text-[0.8125rem] leading-relaxed text-ink line-through decoration-danger/40 scroll-thin">{ex.original}</p>
          </div>
          <div>
            <p className="mb-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-success-ink">Your approved version</p>
            <p className="max-h-56 overflow-y-auto whitespace-pre-wrap rounded-xl bg-success-soft/60 p-3 text-[0.8125rem] leading-relaxed text-ink scroll-thin">{ex.text}</p>
          </div>
        </div>
      ) : (
        <p className="mt-3 max-h-56 overflow-y-auto whitespace-pre-wrap rounded-xl bg-raised p-3 text-[0.8125rem] leading-relaxed text-ink scroll-thin">{ex.text}</p>
      )}
    </li>
  );
}

export function PrefsExamples({ examples, mailboxId, scopeLabel }: { examples: StyleExample[]; mailboxId: string | null; scopeLabel?: string | null }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<StyleExample | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: qk.aiPreferences });

  const add = useMutation({
    mutationFn: (v: { context: string; text: string }) => api('POST /api/ai/examples', { body: { mailboxId, context: v.context, text: v.text } }),
    onSuccess: () => {
      toast.success('Example added');
      setAdding(false);
      void invalidate();
    },
  });
  const edit = useMutation({
    mutationFn: (v: { id: string; context: string; text: string }) => api('PATCH /api/ai/examples/:id', { params: { id: v.id }, body: { context: v.context, text: v.text } }),
    onSuccess: () => {
      toast.success('Example updated');
      setEditing(null);
      void invalidate();
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api('DELETE /api/ai/examples/:id', { params: { id } }),
    onMutate: (id) => setDeletingId(id),
    onSettled: () => setDeletingId(null),
    onSuccess: () => {
      toast.success('Example deleted');
      void invalidate();
    },
    onError: (e) => toast.error(e),
  });

  const askDelete = async (ex: StyleExample) => {
    const ok = await confirm({ title: 'Delete this example?', message: 'The assistant will no longer use it as a reference.', confirmLabel: 'Delete', danger: true });
    if (ok) del.mutate(ex.id);
  };

  const count = `${examples.length} ${examples.length === 1 ? 'example' : 'examples'}`;

  return (
    <Card>
      <CardHeader
        icon={<BookOpenCheck className="h-5 w-5" aria-hidden />}
        title="Learned examples"
        description={`${count} ${scopeLabel ? `for ${scopeLabel}` : 'in the global scope'} — approved replies and corrections used as references.`}
        actions={
          <Button size="sm" variant="primary" icon={<Plus className="h-4 w-4" aria-hidden />} onClick={() => setAdding(true)}>
            Add example
          </Button>
        }
      />
      {examples.length === 0 ? (
        <EmptyState
          className="!py-8"
          icon={<BookOpenCheck className="h-6 w-6" aria-hidden />}
          title="Nothing learned yet"
          description="Add replies you're happy with, or edit an AI draft and choose “Save as example” — your corrections appear here."
        />
      ) : (
        <>
          <CardDivider className="mt-5" />
          <ul className="divide-y divide-line/70">
            {examples.map((ex) => (
              <ExampleRow
                key={ex.id}
                ex={ex}
                deleting={deletingId === ex.id}
                onEdit={() => {
                  edit.reset();
                  setEditing(ex);
                }}
                onDelete={() => void askDelete(ex)}
              />
            ))}
          </ul>
        </>
      )}
      {adding && (
        <ExampleModal
          open
          onClose={() => {
            setAdding(false);
            add.reset();
          }}
          title="Add example"
          initial={{ context: '', text: '' }}
          onSubmit={(v) => add.mutate(v)}
          pending={add.isPending}
          error={add.error}
          submitLabel="Add example"
        />
      )}
      {editing && (
        <ExampleModal
          open
          onClose={() => setEditing(null)}
          title={editing.kind === 'correction' ? 'Edit correction' : 'Edit example'}
          initial={{ context: editing.context, text: editing.text }}
          onSubmit={(v) => edit.mutate({ id: editing.id, ...v })}
          pending={edit.isPending}
          error={edit.error}
          submitLabel="Save changes"
        />
      )}
    </Card>
  );
}
